import { useState, useEffect, useRef } from "preact/hooks"

interface TypewriterResult {
  /** The text to display (may lag behind targetText while animating) */
  displayedText: string
  /** Whether the typewriter is still catching up to the target */
  isAnimating: boolean
}

/**
 * Smoothly reveals text with a typewriter animation.
 * When targetText grows (e.g. from a streaming API), new characters
 * are dripped in at a steady rate rather than appearing all at once.
 * This creates a smooth reading experience even when tokens arrive in bursts.
 *
 * When tokens stream in slowly (real-time), the base speed is fast enough
 * to keep up, so text appears near-instantly. When a burst arrives (e.g.
 * all thinking tokens at once), the adaptive speed drips them over ~2-3s.
 */
export function useTypewriter(targetText: string, charsPerFrame: number = 12): TypewriterResult {
  const [displayedText, setDisplayedText] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const displayedLenRef = useRef(0)
  const targetRef = useRef(targetText)
  const animatingRef = useRef(false)
  const rafRef = useRef(0)

  // Keep target ref in sync for use inside the animation loop
  targetRef.current = targetText

  useEffect(() => {
    // Reset immediately when text is cleared
    if (targetText === "") {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      displayedLenRef.current = 0
      animatingRef.current = false
      setDisplayedText("")
      setIsAnimating(false)
      return
    }

    // Already caught up, nothing to animate
    if (displayedLenRef.current >= targetText.length) return

    // Start the animation loop if not already running
    if (animatingRef.current) return
    animatingRef.current = true
    setIsAnimating(true)

    function tick() {
      const target = targetRef.current
      const currentLen = displayedLenRef.current

      if (currentLen >= target.length) {
        animatingRef.current = false
        setIsAnimating(false)
        return
      }

      // Adaptive speed: go faster when the buffer is large to avoid
      // falling too far behind, but slow enough to look animated
      const remaining = target.length - currentLen
      const speed = remaining > 500
        ? charsPerFrame * 4
        : remaining > 200
          ? charsPerFrame * 3
          : remaining > 50
            ? charsPerFrame * 2
            : charsPerFrame

      const newLen = Math.min(currentLen + speed, target.length)
      displayedLenRef.current = newLen
      setDisplayedText(target.slice(0, newLen))

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [targetText, charsPerFrame])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return { displayedText, isAnimating }
}
